import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Logo } from '../../components/Logo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

export default function LoginPage() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const user = await login(formData.email, formData.password);
            toast.success('Welcome back!');
            
            if (user.role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/dashboard');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-surface-app flex">
            {/* Left side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{ 
                        backgroundImage: 'url(https://images.unsplash.com/photo-1770520215615-af0f13c7fab8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHJlZCUyMGJsYWNrJTIwZGlnaXRhbCUyMG5ldHdvcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3MDczMTAxNHww&ixlib=rb-4.1.0&q=85)'
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-surface-app via-surface-app/80 to-brand-red/20" />
                <div className="relative z-10 flex flex-col justify-center px-16">
                    <Logo size="large" className="mb-8" />
                    <h1 className="text-5xl font-heading font-bold text-white leading-tight mb-6">
                        ABM Performance<br />
                        <span className="text-brand-red">Dashboard</span>
                    </h1>
                    <p className="text-lg text-content-secondary max-w-md">
                        Track your Account-Based Marketing campaigns across LinkedIn, Email, Ads, and Inbound channels.
                    </p>
                    
                    <div className="mt-12 flex gap-8">
                        <div>
                            <p className="text-3xl font-heading font-bold text-brand-red">150+</p>
                            <p className="text-sm text-content-muted">Target Accounts</p>
                        </div>
                        <div>
                            <p className="text-3xl font-heading font-bold text-white">4</p>
                            <p className="text-sm text-content-muted">Active Channels</p>
                        </div>
                        <div>
                            <p className="text-3xl font-heading font-bold text-white">30%</p>
                            <p className="text-sm text-content-muted">MQL Conversion</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right side - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center px-8">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-8">
                        <Logo size="large" />
                    </div>

                    <h2 className="text-3xl font-heading font-bold text-white mb-2">
                        Welcome Back
                    </h2>
                    <p className="text-content-secondary mb-8">
                        Sign in to access your ABM dashboard
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-content-secondary">
                                Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@company.com"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="input-base h-12"
                                data-testid="login-email-input"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-content-secondary">
                                Password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Enter your password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="input-base h-12 pr-12"
                                    data-testid="login-password-input"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-content-muted hover:text-white"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="btn-primary w-full h-12 text-base"
                            disabled={loading}
                            data-testid="login-submit-btn"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Signing in...
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    Sign In
                                    <ArrowRight className="w-5 h-5" />
                                </span>
                            )}
                        </Button>
                    </form>

                    <div className="mt-8 text-center">
                        <p className="text-content-secondary">
                            Don't have an account?{' '}
                            <Link 
                                to="/register" 
                                className="text-brand-red hover:text-brand-red-hover font-medium"
                                data-testid="register-link"
                            >
                                Get Started
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
